import {useEffect, useRef} from "react";
import type {Location, PublicTaxiOrderConfiguration} from "@/api/apiClient";
import {useConfiguration} from "@/features/configuration";
import {useEnrichLocation} from "@/features/location-suggestions/hooks/useEnrichLocation";
import type {LocationWithCoordinates} from "@/features/location-suggestions/utils/types";
import type {OrderDraft} from "@/features/order/utils/types";
import {usePreservedNavigate} from "@/lib/router";
import {usePersistedOrderDraft} from "../hooks/usePersistedOrderDraft";
import {useTaxiOverview} from "../hooks/useTaxiOverview";
import {useUrlOrderParams} from "../hooks/useUrlOrderParams";
import {checkMinimalSphericalDistance} from "../utils/validation";
import {OrderContext} from "./OrderContext";

const initialOrderDraft: OrderDraft = {
    pickup: undefined,
    dropoff: undefined,
    name: "",
    phoneNumber: "",
};

async function enrichFixedLocations(
    configuration: PublicTaxiOrderConfiguration,
    enrichLocation: (location: Location) => Promise<LocationWithCoordinates>
): Promise<Partial<OrderDraft>> {
    const updates: Partial<OrderDraft> = {};

    if (configuration.fixedPickup) {
        try {
            const enrichedPickup = await enrichLocation(configuration.fixedPickup);
            const locationType = configuration.fixedPickup.type;
            updates.pickup = {
                location: enrichedPickup,
                type: locationType === "ADDRESS" ? "SEARCH" : locationType,
                complete: true,
            };
        } catch (error) {
            console.error("Failed to enrich fixedPickup with coordinates:", error);
        }
    }

    if (configuration.fixedDestination) {
        try {
            const enrichedDestination = await enrichLocation(configuration.fixedDestination);
            const locationType = configuration.fixedDestination.type;
            updates.dropoff = {
                location: enrichedDestination,
                type: locationType === "ADDRESS" ? "SEARCH" : locationType,
                complete: true,
            };
        } catch (error) {
            console.error("Failed to enrich fixedDestination with coordinates:", error);
        }
    }

    return updates;
}

export function OrderProvider({children}: {children: React.ReactNode}) {
    const navigate = usePreservedNavigate();
    const hasInitialized = useRef(false);
    const appliedConfigRef = useRef<string | null>(null);
    const {data: configuration} = useConfiguration();
    const {enrichLocation} = useEnrichLocation();
    const {hasUrlParams, urlDraft, hasPickupCoords, hasDropOffCoords, resolveLocations} =
        useUrlOrderParams();

    const {orderDraft, setOrderDraft, clearPersistedData} = usePersistedOrderDraft(
        hasUrlParams ? {...initialOrderDraft, ...urlDraft} : initialOrderDraft,
        {skipRestore: hasUrlParams}
    );

    // Create a stable key for the current fixed locations configuration
    const fixedLocationsKey = configuration
        ? `${configuration.fixedPickup?.id}-${configuration.fixedDestination?.id}`
        : null;

    useEffect(() => {
        // Skip if no configuration or if we already applied this configuration
        if (
            !configuration ||
            !fixedLocationsKey ||
            appliedConfigRef.current === fixedLocationsKey
        ) {
            return;
        }

        // Capture key before async operation to detect stale responses
        const capturedKey = fixedLocationsKey;
        appliedConfigRef.current = fixedLocationsKey;

        enrichFixedLocations(configuration, enrichLocation).then((updates) => {
            // Guard against stale enrichment overwriting newer config
            if (appliedConfigRef.current !== capturedKey) {
                return;
            }
            if (Object.keys(updates).length > 0) {
                setOrderDraft((prev) => ({
                    ...prev,
                    // Don't overwrite locations provided via URL params
                    ...(hasPickupCoords ? {} : updates.pickup ? {pickup: updates.pickup} : {}),
                    ...(hasDropOffCoords ? {} : updates.dropoff ? {dropoff: updates.dropoff} : {}),
                }));
            }
        });
    }, [
        configuration,
        setOrderDraft,
        enrichLocation,
        fixedLocationsKey,
        hasPickupCoords,
        hasDropOffCoords,
    ]);

    // Resolve URL param coordinates into locations via reverse geocoding.
    // hasInitialized is set before the async call to prevent double invocation in StrictMode.
    useEffect(() => {
        if (hasInitialized.current || (!hasPickupCoords && !hasDropOffCoords)) return;
        hasInitialized.current = true;

        resolveLocations()
            .then((locationUpdates) => {
                setOrderDraft((prev) => ({...prev, ...locationUpdates}));

                if (locationUpdates.pickup && locationUpdates.dropoff) {
                    navigate("/taxi/overview", {replace: true});
                }
            })
            .catch((error) => {
                console.error("Failed to resolve URL param locations:", error);
            });
    }, [hasPickupCoords, hasDropOffCoords, resolveLocations, setOrderDraft, navigate]);

    const resetOrderDraft = () => {
        setOrderDraft(initialOrderDraft);
        clearPersistedData();
    };

    const pickup = orderDraft.pickup?.location;
    const dropoff = orderDraft.dropoff?.location;
    const isViableRoute =
        pickup && dropoff ? checkMinimalSphericalDistance(pickup, dropoff) : false;

    const {data: overview} = useTaxiOverview({
        pickupLocation: orderDraft.pickup,
        dropoffLocation: orderDraft.dropoff,
        isViableRoute,
    });

    return (
        <OrderContext.Provider
            value={{
                orderDraft,
                setOrderDraft,
                resetOrderDraft,
                overview,
                isViableRoute,
            }}
        >
            {children}
        </OrderContext.Provider>
    );
}
